/**
 * PostCallModal — global modal shown after a call ends when the agent
 * is not on the VoIP page. Collects quality rating, outcome, notes,
 * and offers lead creation for unknown callers.
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PhoneOff, Star, UserPlus, X, Check } from 'lucide-react'
import { voipApi } from '../../api/endpoints'

interface PostCallModalProps {
  callOutcome: string
  callId: number | null
  duration: number
  contactName: string | null
  callerNumber: string | null
  isUnknownInbound: boolean
  onClose: () => void
}

export default function PostCallModal({
  callOutcome,
  callId,
  duration,
  contactName,
  callerNumber,
  isUnknownInbound,
  onClose,
}: PostCallModalProps) {
  const queryClient = useQueryClient()
  const [quality, setQuality] = useState(0)
  const [outcome, setOutcome] = useState('')
  const [notes, setNotes] = useState('')
  const [ratingSaved, setRatingSaved] = useState(false)

  // Lead creation state (unknown callers)
  const [leadName, setLeadName] = useState('')
  const [leadCompany, setLeadCompany] = useState('')
  const [leadEmail, setLeadEmail] = useState('')
  const [leadCreated, setLeadCreated] = useState<{ name: string; count: number } | null>(null)

  const rateMutation = useMutation({
    mutationFn: (data: { call_outcome?: string; call_quality_score?: number; outcome_notes?: string }) =>
      voipApi.rateCall(callId!, data),
    onSuccess: () => {
      setRatingSaved(true)
      queryClient.invalidateQueries({ queryKey: ['voip-calls'] })
    },
  })

  const createLeadMutation = useMutation({
    mutationFn: () => voipApi.createLeadFromCall(callId!, {
      name: leadName,
      ...(leadCompany ? { company: leadCompany } : {}),
      ...(leadEmail ? { email: leadEmail } : {}),
    }),
    onSuccess: (result) => {
      setLeadCreated({ name: result.lead_name, count: result.linked_count })
      queryClient.invalidateQueries({ queryKey: ['voip-calls'] })
    },
  })

  const handleSaveRating = () => {
    if (!callId) return
    rateMutation.mutate({
      ...(outcome ? { call_outcome: outcome } : {}),
      ...(quality > 0 ? { call_quality_score: quality } : {}),
      ...(notes ? { outcome_notes: notes } : {}),
    })
  }

  const fmtDuration = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`

  const isConnected = callOutcome === 'connected'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="fixed inset-0 bg-black/40" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <PhoneOff size={16} className={isConnected ? 'text-green-600' : 'text-gray-400'} />
            <span className="text-sm font-semibold text-gray-900">
              Call Ended {isConnected ? `- ${fmtDuration(duration)}` : ''}
            </span>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Call info */}
          <div className="text-sm text-gray-600">
            {contactName && <span className="font-medium text-gray-900">{contactName}</span>}
            {!contactName && callerNumber && <span className="font-mono">{callerNumber}</span>}
            {' '}&mdash;{' '}
            {isConnected ? 'Connected' : callOutcome === 'cancelled' ? 'Cancelled' : callOutcome === 'not_connected' ? 'Not Connected' : 'Failed'}
          </div>

          {/* Rating form (connected calls with callId) */}
          {isConnected && callId && !ratingSaved && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Call Quality</label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button key={star} onClick={() => setQuality(star)} className="p-0.5">
                      <Star size={20} className={star <= quality ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} />
                    </button>
                  ))}
                  {quality > 0 && (
                    <span className="text-xs text-gray-500 ml-1">
                      {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][quality]}
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Outcome</label>
                <select
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value)}
                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="">Select outcome...</option>
                  <option value="resolved">Resolved</option>
                  <option value="escalated">Escalated</option>
                  <option value="transferred">Transferred</option>
                  <option value="voicemail">Voicemail</option>
                  <option value="abandoned">Abandoned</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
                  placeholder="Call notes..."
                />
              </div>

              <button
                onClick={handleSaveRating}
                disabled={rateMutation.isPending || (quality === 0 && !outcome && !notes)}
                className="w-full px-3 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {rateMutation.isPending ? 'Saving...' : 'Save Rating'}
              </button>
            </div>
          )}

          {ratingSaved && (
            <div className="flex items-center gap-2 text-green-600 text-sm">
              <Check size={14} /> Rating saved
            </div>
          )}

          {/* Unknown caller lead creation */}
          {isUnknownInbound && callId && !leadCreated && (
            <div className="pt-3 border-t border-gray-200 space-y-3">
              <div className="flex items-center gap-2">
                <UserPlus size={14} className="text-amber-600" />
                <span className="text-sm font-medium text-amber-800">Create Lead from Unknown Caller?</span>
              </div>
              {callerNumber && (
                <p className="text-xs text-gray-500">Phone: <span className="font-mono">{callerNumber}</span></p>
              )}
              <input
                type="text"
                value={leadName}
                onChange={(e) => setLeadName(e.target.value)}
                placeholder="Name (required)"
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-indigo-500"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={leadCompany}
                  onChange={(e) => setLeadCompany(e.target.value)}
                  placeholder="Company"
                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <input
                  type="email"
                  value={leadEmail}
                  onChange={(e) => setLeadEmail(e.target.value)}
                  placeholder="Email"
                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <button
                onClick={() => createLeadMutation.mutate()}
                disabled={!leadName.trim() || createLeadMutation.isPending}
                className="w-full px-3 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {createLeadMutation.isPending ? 'Creating...' : 'Create Lead'}
              </button>
              {createLeadMutation.error && (
                <p className="text-xs text-red-600">{(createLeadMutation.error as Error).message}</p>
              )}
            </div>
          )}

          {leadCreated && (
            <div className="flex items-center gap-2 text-green-600 text-sm pt-2 border-t border-gray-200">
              <Check size={14} />
              <div>
                <span>Lead created: {leadCreated.name}</span>
                {leadCreated.count > 1 && (
                  <p className="text-xs text-green-500">{leadCreated.count} calls linked</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            {ratingSaved || !isConnected ? 'Close' : 'Skip'}
          </button>
        </div>
      </div>
    </div>
  )
}
